import { gsap, motion, seconds, useReducedMotion } from '@gestao/ui'
import { useLayoutEffect, useRef } from 'react'
import { Outlet } from 'react-router'

import { LoginHero } from './login-hero'

/** O momento de entrada acontece uma vez por carga da página (voltar ao login não repete). */
let introPlayed = false

/** Só para testes. */
export function resetLoginIntro(): void {
  introPlayed = false
}

type IntroState = 'idle' | 'running' | 'done'

/** Deriva das formas: 10 s para cada lado (loop de 20 s com yoyo), amplitude pequena. */
const DRIFT = [
  { xPercent: 7, yPercent: 5, rotation: 8, scale: 1.06 },
  { xPercent: -6, yPercent: 7, rotation: -6, scale: 1.04 },
  { xPercent: 5, yPercent: -6, rotation: 10, scale: 1.08 },
] as const

/**
 * Tela dividida das rotas de acesso (login, esqueci a senha, redefinir): painel petróleo à
 * esquerda (58%) e o formulário de 400 px à direita. Abaixo de 960 px o painel vira uma faixa
 * de 160 px no topo. O layout fica montado entre as três rotas, então a animação não reinicia.
 *
 * Momento orquestrado único (design-system §6), ~1,2 s: formas aparecem e começam a derivar,
 * nome, frase e cliente entram com fade + 12 px em cascata de 80 ms, e o formulário entra
 * 200 ms depois. Com prefers-reduced-motion, tudo aparece parado; ao primeiro toque ou tecla,
 * a deriva das formas para.
 */
export function AuthLayout() {
  const shapesRef = useRef<HTMLDivElement>(null)
  const copyRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<HTMLDivElement>(null)
  const rootRef = useRef<HTMLDivElement>(null)
  const reducedMotion = useReducedMotion()

  useLayoutEffect(() => {
    const shapes = shapesRef.current
    const copy = copyRef.current
    const form = formRef.current
    const root = rootRef.current
    if (!shapes || !copy || !form || !root) return
    // Estado da entrada no DOM (para testes e2e esperarem o fim), sem re-render.
    const setIntro = (state: IntroState) => {
      root.dataset.intro = state
    }
    if (reducedMotion) {
      setIntro('done')
      return
    }

    const drifts: gsap.core.Tween[] = []
    const ctx = gsap.context(() => {
      shapes.querySelectorAll<HTMLElement>('[data-shape]').forEach((shape, index) => {
        drifts.push(
          gsap.to(shape, {
            ...DRIFT[index % DRIFT.length],
            duration: 10,
            ease: motion.ease.state,
            yoyo: true,
            repeat: -1,
          }),
        )
      })

      if (introPlayed) {
        setIntro('done')
        return
      }

      setIntro('running')
      const offset = motion.offset.hero
      const textStart = 0.12
      const cascade = 0.08
      const lines = Array.from(copy.children)
      gsap
        .timeline({
          defaults: { ease: motion.ease.entrance, duration: seconds('hero') },
          onComplete: () => {
            introPlayed = true
            setIntro('done')
          },
        })
        .fromTo(shapes, { opacity: 0 }, { opacity: 1, duration: 1.2, ease: motion.ease.state }, 0)
        .fromTo(
          lines,
          { opacity: 0, y: offset },
          { opacity: 1, y: 0, stagger: cascade, clearProps: 'opacity,transform' },
          textStart,
        )
        .fromTo(
          form,
          { opacity: 0, y: offset },
          { opacity: 1, y: 0, clearProps: 'opacity,transform' },
          textStart + cascade * (lines.length - 1) + 0.2,
        )
    })

    // A deriva é decorativa e contínua: assim que a pessoa começa a usar a tela (tecla ou
    // clique), ela desacelera até parar, para não disputar atenção com o formulário.
    // ctx.add roda a função dentro do contexto: o revert desfaz também essa desaceleração.
    const settle = () => {
      ctx.add(() => {
        for (const drift of drifts) {
          gsap.to(drift, { timeScale: 0, duration: seconds('hero'), ease: motion.ease.state })
        }
      })
    }
    root.addEventListener('keydown', settle, { once: true })
    root.addEventListener('pointerdown', settle, { once: true })

    return () => {
      root.removeEventListener('keydown', settle)
      root.removeEventListener('pointerdown', settle)
      ctx.revert()
    }
  }, [reducedMotion])

  return (
    <div
      ref={rootRef}
      data-intro="idle"
      className="flex min-h-dvh flex-col bg-canvas login:flex-row"
    >
      <LoginHero shapesRef={shapesRef} copyRef={copyRef} />
      {/*
       * Celular e tablet (celular-1-31): o formulário encosta na faixa, alinhado à esquerda com o
       * título dela (mesmo recuo de 24 px); a partir de 960 px, centralizado ao lado do painel.
       */}
      <main className="flex flex-1 items-start justify-start px-6 pt-8 pb-10 login:items-center login:justify-center login:px-12 login:py-10">
        <div ref={formRef} className="w-full max-w-100">
          <Outlet />
        </div>
      </main>
    </div>
  )
}
